/**
 * Service pour l'upload de fichiers volumineux par chunks
 * Basé sur l'article Medium: Uploading Large Videos with Node.js
 *
 * Caractéristiques:
 * - Découpage automatique en chunks de taille configurable
 * - Retry automatique en cas d'erreur (3 tentatives par défaut)
 * - Callbacks de progression pour l'UI
 * - Gestion d'erreurs robuste
 */
class ChunkedUploadService {
  /**
   * @param {Object} options - Options de configuration
   * @param {number} options.chunkSize - Taille des chunks en octets (défaut: 10 MB)
   * @param {number} options.maxRetries - Nombre max de tentatives par chunk (défaut: 3)
   * @param {number} options.retryDelay - Délai initial entre les tentatives en ms (défaut: 1000)
   */
  constructor(options = {}) {
    this.chunkSize = options.chunkSize || 10 * 1024 * 1024 // 10 MB par défaut
    this.maxRetries = options.maxRetries || 3
    this.retryDelay = options.retryDelay || 1000 // 1 seconde
  }

  /**
   * Upload un fichier en le découpant en chunks
   * @param {File} file - Fichier à uploader
   * @param {Object} options - Options d'upload
   * @param {string} options.jwt - Token JWT pour l'authentification
   * @param {Object} options.additionalData - Données additionnelles à envoyer (language, etc.)
   * @param {Function} options.onProgress - Callback de progression (progress, currentChunk, totalChunks)
   * @param {Function} options.onChunkComplete - Callback après chaque chunk (chunkIndex, totalChunks)
   * @returns {Promise<Object>} Résultat de l'upload
   */
  async uploadFile(file, options = {}) {
    const totalChunks = Math.ceil(file.size / this.chunkSize)

    // Générer un uploadId unique pour cet upload
    const uploadId = this.generateUploadId()

    // eslint-disable-next-line no-console
    console.log(
      `Début de l'upload de ${file.name} (${this.formatFileSize(file.size)}) en ${totalChunks} chunks [uploadId: ${uploadId}]`
    )

    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      const start = chunkIndex * this.chunkSize
      const end = Math.min(start + this.chunkSize, file.size)
      const chunk = file.slice(start, end)

      // eslint-disable-next-line no-console
      console.log(
        `Upload du chunk ${chunkIndex + 1}/${totalChunks} (${this.formatFileSize(chunk.size)})`
      )

      const chunkResult = await this.uploadChunkWithRetry(chunk, {
        chunkIndex,
        totalChunks,
        filename: file.name,
        uploadId,
        ...options,
      })

      // Si c'est le dernier chunk, le serveur retourne les informations du fichier fusionné
      if (chunkIndex === totalChunks - 1 && chunkResult.data) {
        // eslint-disable-next-line no-console
        console.log(`Upload de ${file.name} terminé avec succès`)
        return chunkResult // Retourner la réponse du serveur avec le chemin du fichier
      }

      // Callback après chaque chunk complété
      if (options.onChunkComplete) {
        options.onChunkComplete(chunkIndex, totalChunks)
      }

      // Callback de progression
      if (options.onProgress) {
        const progress = ((chunkIndex + 1) / totalChunks) * 100
        options.onProgress(progress, chunkIndex + 1, totalChunks)
      }
    }

    // eslint-disable-next-line no-console
    console.log(`Upload de ${file.name} terminé avec succès`)

    return { success: true, filename: file.name }
  }

  /**
   * Upload un chunk avec retry automatique en cas d'échec
   * @param {Blob} chunk - Chunk à uploader
   * @param {Object} metadata - Métadonnées du chunk
   * @returns {Promise<Object>} Résultat de l'upload du chunk
   */
  async uploadChunkWithRetry(chunk, metadata) {
    let lastError = null

    for (let attempt = 0; attempt < this.maxRetries; attempt++) {
      try {
        const result = await this.uploadChunk(chunk, metadata)
        return result // Succès, sortir
      } catch (error) {
        lastError = error
        console.warn(
          `Chunk ${metadata.chunkIndex + 1} failed (attempt ${attempt + 1}/${this.maxRetries}):`,
          error.message
        )

        if (attempt < this.maxRetries - 1) {
          // Délai exponentiel: 1s, 2s, 4s
          const delay = this.retryDelay * Math.pow(2, attempt)
          console.log(`Retry dans ${delay}ms...`)
          await this.sleep(delay)
        }
      }
    }

    // Toutes les tentatives ont échoué
    throw new Error(
      `Échec de l'upload du chunk ${metadata.chunkIndex + 1} après ${this.maxRetries} tentatives: ${lastError.message}`
    )
  }

  /**
   * Upload un seul chunk vers le serveur
   * @param {Blob} chunk - Chunk à uploader
   * @param {Object} metadata - Métadonnées du chunk
   * @returns {Promise<Object>} Résultat de l'upload
   */
  async uploadChunk(chunk, metadata) {
    const formData = new FormData()

    // IMPORTANT: Ajouter les fields AVANT le file pour que Busboy les reçoive en premier
    formData.append('chunk', metadata.chunkIndex.toString())
    formData.append('totalChunks', metadata.totalChunks.toString())
    formData.append('originalname', metadata.filename)
    formData.append('uploadId', metadata.uploadId)

    // Ajouter les données additionnelles (language, etc.)
    if (metadata.additionalData) {
      Object.keys(metadata.additionalData).forEach((key) => {
        formData.append(key, metadata.additionalData[key])
      })
    }

    // Ajouter le file EN DERNIER
    formData.append('file', chunk, metadata.filename)

    // eslint-disable-next-line no-console
    console.log(
      `Envoi chunk ${metadata.chunkIndex}/${metadata.totalChunks - 1} (${this.formatFileSize(chunk.size)})`
    )

    const response = await fetch('/api/upload/chunk', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${metadata.jwt}`,
      },
      body: formData,
    })

    if (!response.ok) {
      let errorData
      try {
        errorData = await response.json()
      } catch {
        errorData = { message: `HTTP Error ${response.status}` }
      }

      throw new Error(
        errorData.error?.message || errorData.message || 'Chunk upload failed'
      )
    }

    return await response.json()
  }

  /**
   * Utilitaire pour attendre un délai
   * @param {number} ms - Délai en millisecondes
   * @returns {Promise<void>}
   */
  sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  /**
   * Génère un ID unique pour l'upload
   * Utilise une approche similaire à nanoid() mais compatible navigateur
   * @returns {string} ID unique
   */
  generateUploadId() {
    const timestamp = Date.now().toString(36)
    const randomStr = Math.random().toString(36).substring(2, 15)
    return `${timestamp}-${randomStr}`
  }

  /**
   * Formate une taille de fichier en format lisible
   * @param {number} bytes - Taille en octets
   * @returns {string} Taille formatée (ex: "2.5 MB")
   */
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes'

    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
  }
}

export default ChunkedUploadService
